import React, {FC, forwardRef, useCallback, useState} from 'react';

import {cn} from '@bem-react/classname';
import {DatePicker, DatePickerProps} from '@gravity-ui/date-components';
import {DateTime, dateTimeParse} from '@gravity-ui/date-utils';
import QuestionLayout, {QuestionLayoutType} from 'ui/components/QuestionLayout';
import RangeDatePicker from 'ui/components/RangeDatePicker';
import i18n from 'ui/i18n';
import isDateRange from 'ui/utils/isDateRange';

import {DateQuestionProps} from './DateQuestion.types';

import './DateQuestion.scss';

const cnDateQuestion = cn('DateQuestion');

const iframe = window.YC?.iframe;

const DateQuestion: FC<DateQuestionProps> = forwardRef<HTMLDivElement, DateQuestionProps>(
    function DateQuestion({error, className, onUpdate, range, id, min, max, ...props}, ref) {
        const [date, setDate] = useState<DateTime | null>(() => {
            if (props.value && typeof props.value === 'string') {
                return dateTimeParse(props.value) || null;
            }
            return null;
        });
        const commonProps = React.useMemo<DatePickerProps>(
            () => ({
                size: 'l',
                className: cnDateQuestion('Input', {iframe}),
                validationState: error ? 'invalid' : undefined,
                minValue: min ? dateTimeParse(min) : undefined,
                maxValue: max ? dateTimeParse(max) : undefined,
                hasClear: true,
                id,
            }),
            [error, id, max, min],
        );

        const onUpdateSingle = useCallback(
            (value: DateTime | null) => {
                if (onUpdate) {
                    onUpdate(value ? value.format('YYYY-MM-DD') : null);
                }
                setDate(value);
            },
            [onUpdate],
        );

        return (
            <QuestionLayout
                {...props}
                error={error}
                type={QuestionLayoutType.Column}
                className={cnDateQuestion('', [className])}
                control={
                    <div className={cnDateQuestion('Control')} ref={ref}>
                        {range ? (
                            <RangeDatePicker
                                {...commonProps}
                                fieldValue={isDateRange(props.value) ? props.value : undefined}
                                onUpdateField={onUpdate}
                            />
                        ) : (
                            <DatePicker
                                {...commonProps}
                                value={date}
                                onUpdate={onUpdateSingle}
                                placeholder={i18n('survey-page', 'date-placeholder')}
                            />
                        )}
                    </div>
                }
            />
        );
    },
);
export default DateQuestion;
